/**
 * What is still missing from a discharge summary.
 *
 * Advisory, not blocking: this names gaps rather than guessing whether they
 * matter. A patient can legitimately be discharged on no medication, so an
 * empty take-home list is not a gap — an unnamed diagnosis is.
 *
 * Before this existed there was no completeness check anywhere in the wizard:
 * an empty summary compiled, previewed and finalized without a single
 * objection.
 */

import { isLamaOrDama } from "../constants/dischargeTypes";

const hasText = (v) => typeof v === "string" && v.trim() !== "";

const hasEntries = (v) =>
  Array.isArray(v) && v.some((x) => (typeof x === "string" ? x.trim() !== "" : Boolean(x)));

const CHECKS = [
  {
    key: "final_diagnosis",
    label: "Final diagnosis",
    test: (s) => hasEntries(s?.final_diagnosis),
  },
  {
    key: "presenting_complaints",
    label: "Presenting complaints",
    test: (s) => hasEntries(s?.history?.presenting_complaints),
  },
  {
    key: "condition_at_discharge",
    label: "Condition at discharge",
    test: (s) => hasText(s?.header?.discharge_details?.condition_at_discharge),
  },
  {
    key: "vital_signs",
    label: "Vital signs at discharge",
    test: (s) => {
      const v = s?.header?.discharge_details?.vital_signs || {};
      return ["bp", "pulse", "temperature", "spo2"].some((k) => hasText(String(v[k] ?? "")));
    },
  },
  {
    key: "follow_up",
    label: "Follow-up plan",
    test: (s) =>
      hasText(s?.advices?.follow_up?.department) ||
      hasText(String(s?.advices?.follow_up?.after_days ?? "")) ||
      hasText(s?.advices?.follow_up?.purpose),
  },
  { key: "prepared_by", label: "Prepared by", test: (s) => hasText(s?.prepared_by?.name) },
  { key: "checked_by", label: "Checked by", test: (s) => hasText(s?.checked_by?.name) },
];

const LAMA_DAMA_CHECKS = [
  {
    key: "lama_dama_reason",
    label: "LAMA/DAMA reason",
    test: (s) => hasText(s?.header?.lama_dama_details?.reason),
  },
  {
    key: "lama_dama_risks",
    label: "Risk counseling notes",
    test: (s) => hasText(s?.header?.lama_dama_details?.risks_explained_notes),
  },
  {
    key: "lama_dama_surrogate",
    label: "Signer / surrogate name",
    test: (s) => hasText(s?.header?.lama_dama_details?.surrogate_name),
  },
  {
    key: "lama_dama_declaration",
    label: "Declaration acknowledgment",
    test: (s) => s?.header?.lama_dama_details?.declaration_acknowledged === true,
  },
];

export const unmetCompletenessItems = (summary) => {
  const activeChecks = isLamaOrDama(summary?.header?.discharge_type)
    ? [...CHECKS, ...LAMA_DAMA_CHECKS]
    : CHECKS;
  return activeChecks.filter((c) => !c.test(summary || {})).map(({ key, label }) => ({ key, label }));
};

export default unmetCompletenessItems;
